import React from "react";
import { nudgeText } from "../../../core/data/auditsPage";
import { formatDate } from "../../../core/data/marketOverviewText";
import { INDUSTRY } from "../../../core/theme/auditsTheme";
import TargetRings from "./TargetRings";
import SearchesGraph from "./SearchesGraph";
import ChannelMixBar from "./ChannelMixBar";

// Top left: the selected industry's Industry Audit progress. Rings, numbers
// and nudge come straight from the roll-up (nicheRollup); nothing is counted
// here. Unsorted and No niche never get rings: they don't count.
//   industry: from the list ({ name, nicheId, snapshots, special })
//   niche:    the roll-up's entry (nicheNumbers), or null (No niche, loading, failed)
//   rollupState: useNicheRollup's { status, rollup }
export default function IndustryAuditPanel({ industry, niche, rollupState, onNewScan }) {
  const ready = rollupState.status === "ready";

  return (
    <section aria-label="Industry Audit" className={`${INDUSTRY.panel} ${INDUSTRY.audit}`}>
      <div className={INDUSTRY.auditHead}>
        <div className="min-w-0">
          <div className={INDUSTRY.label}>{industry.special ? "Snapshots" : "Industry Audit"}</div>
          <h1 className={INDUSTRY.auditName}>{industry.name}</h1>
          {niche && niche.insightSince && (
            <div className={INDUSTRY.auditSince}>Insight since {formatDate(niche.insightSince)}{niche.estimated ? " (estimated)" : ""}</div>
          )}
        </div>
        {!industry.special && ready && niche && (
          <span className={niche.unlocked ? INDUSTRY.pillUnlocked : INDUSTRY.pillBuilding}>{niche.unlocked ? "Unlocked" : "Building"}</span>
        )}
      </div>

      {!industry.nicheId ? (
        <SpecialNumbers industry={industry} niche={null} />
      ) : rollupState.status === "loading" ? (
        <p className={INDUSTRY.note}>Loading the Industry Audit numbers…</p>
      ) : !ready ? (
        <p className={INDUSTRY.warning}>The Industry Audit numbers couldn't be loaded. Your Snapshots are unaffected.</p>
      ) : industry.special ? (
        <SpecialNumbers industry={industry} niche={niche} />
      ) : (
        <NicheProgress niche={niche} rollup={rollupState.rollup} onNewScan={onNewScan} />
      )}
    </section>
  );
}

function NicheProgress({ niche, rollup, onNewScan }) {
  const nudge = nudgeText(niche);
  const est = niche.estimated ? " · estimated" : "";
  const target = t => (niche.progress ? `target ${niche.progress[t].target.toLocaleString("en-GB")}${est}` : est.slice(3));

  return (
    <>
      {!rollup.targetsLoaded && <p className={INDUSTRY.warning}>The unlock targets couldn't be loaded, so every niche shows as locked.</p>}
      {!rollup.complete && <p className={INDUSTRY.warning}>Some rows didn't load, so these numbers may be short.</p>}
      <div className={INDUSTRY.ringsRow}>
        <TargetRings progress={niche.progress} />
        <div className={INDUSTRY.stats}>
          <Stat swatch={0} label="Searches" value={niche.searches} sub={target("searches")} />
          <Stat swatch={1} label="Locations" value={niche.locations} sub={target("locations")} />
          <Stat swatch={2} label="Keywords" value={niche.keywords} sub={target("keywords")} />
          <Stat swatch={3} label="Advertisers found" value={niche.advertisers} sub="across every channel" />
        </div>
      </div>
      <div className={INDUSTRY.graphs}>
        <SearchesGraph points={niche.searchesOverTime} />
        <ChannelMixBar mix={niche.channelMix} />
      </div>
      {nudge && (
        <div className={INDUSTRY.nudgeRow}>
          <div className={INDUSTRY.nudge}>{nudge}</div>
          {!niche.unlocked && <button type="button" onClick={onNewScan} className={INDUSTRY.startQuiet}>Start search</button>}
        </div>
      )}
    </>
  );
}

// Unsorted (the roll-up's numbers, no rings) and No niche (Snapshot count only).
function SpecialNumbers({ industry, niche }) {
  const est = niche && niche.estimated ? "estimated" : "";
  return (
    <>
      <div className={INDUSTRY.stats}>
        <Stat label="Snapshots" value={industry.snapshots} />
        {niche && (
          <>
            <Stat label="Searches" value={niche.searches} sub={est} />
            <Stat label="Locations" value={niche.locations} sub={est} />
            <Stat label="Keywords" value={niche.keywords} sub={est} />
            <Stat label="Advertisers found" value={niche.advertisers} sub="across every channel" />
          </>
        )}
      </div>
      <p className={INDUSTRY.note}>
        {industry.nicheId ? "Snapshots in Unsorted" : "Snapshots with no niche"} don't count towards an Industry Audit.
      </p>
    </>
  );
}

function Stat({ swatch, label, value, sub }) {
  return (
    <div>
      <div className={INDUSTRY.statLabel}>
        {swatch != null && <span aria-hidden="true" className={`w-2 h-2 rounded-full ${INDUSTRY.swatch[swatch]}`} />}
        {label}
      </div>
      <div className={INDUSTRY.statValue}>{Number(value || 0).toLocaleString("en-GB")}</div>
      {sub && <div className={INDUSTRY.statSub}>{sub}</div>}
    </div>
  );
}
